/**
 * Navigation Difficulty Item Component
 *
 * Single Responsibility: Displays a single navigation difficulty item
 * Follows SOLID principles:
 * - Single Responsibility: Only responsible for rendering one navigation difficulty item
 * - Open/Closed: Open for extension via props, closed for modification
 * - Dependency Inversion: Depends on shared component interface
 */

import React from 'react';
import { BulletListItem, BulletListItemData } from '../shared/BulletListItem';

export interface NavigationDifficultyItemProps {
  /**
   * Navigation difficulty item data with heading and description
   * If heading is not provided, the entire text will be used as description
   */
  item: BulletListItemData | { text: string; id?: string };
}

/**
 * Navigation Difficulty Item Component
 * Renders a single navigation difficulty item using the shared bullet list item component
 * Supports both structured format (heading + description) and simple text format
 */
export const NavigationDifficultyItem: React.FC<
  NavigationDifficultyItemProps
> = ({ item }) => {
  // Handle both formats: structured (heading + description) or simple text
  // If it's structured with heading and description, use as-is
  if ('heading' in item && 'description' in item) {
    return (
      <BulletListItem
        item={{
          heading: item.heading,
          description: item.description,
          id: item.id,
        }}
      />
    );
  }

  // For simple text format, display as description only (no heading)
  // Use a generic heading or just show the text
  const text = (item as { text: string }).text;
  return (
    <BulletListItem
      item={{
        heading: '',
        description: text,
        id: (item as { id?: string }).id,
      }}
    />
  );
};
